import {
  of,
  Subject,
  catchError,
  debounceTime,
  mergeMap,
  tap,
  map,
  filter,
} from "rxjs";
import apiClient from "./apiClient";
import store from "../state/store";

function getUserMap() {
  const state = store.getState() || {};
  return state.userMap || new Map();
}

function toBatchesOf(maxNum = 1, arr = []) {
  const rest = arr.slice(maxNum);
  if (rest.length > maxNum) {
    return [arr.slice(0, maxNum), ...toBatchesOf(maxNum, rest)];
  }
  if (rest.length) {
    return [arr.slice(0, maxNum), rest];
  }
  return [arr.slice(0, maxNum)];
}

const userService = (() => {
  const delay = 10;
  const maxUserBatch = 50;
  const loadingIds = new Set();
  const fetchIds$ = new Subject();

  let isRegistered = false;
  let idSet = new Set();

  function storeDispatchUsers(users) {
    const initMap = new Map(store.getState().userMap);

    const userMap = Object.entries(users).reduce(
      (a, [id, val]) => a.set(id, val),
      initMap
    );

    store.dispatch({ type: "SET_USER_MAP", userMap });
  }

  function register() {
    fetchIds$
      .pipe(
        tap((id) => idSet.add(id)), // add values to a set of ids until for the period until debounceTime.
        debounceTime(delay), // wait for all components to emit their user.
        map(() => Array.from(idSet)), // get the array of the current unique ssoids.
        tap(() => (idSet = new Set())), // reset the unique ids --- so that users are not fetched again.
        map((ids) => ids.filter((x) => !getUserMap().has(x))), // remove users that have already been resolved
        map((ids) => ids.filter((x) => !loadingIds.has(x))), // remove users that have not been resolved but are loading.
        tap((ids) => ids.forEach((x) => loadingIds.add(x))), // add users to the loading ones.
        map((ids) => toBatchesOf(maxUserBatch, ids)), // break array to smaller arrays if applicable.
        mergeMap((arr) => arr), // flatten array of arrays. (new emission for each element or array on the input)
        filter((arr) => arr.length), // do not make request if empty array
        mergeMap((ssoIdBatch) => {
          // make a request for each array of user ids.
          return apiClient.getCompanyInfo(ssoIdBatch).pipe(
            catchError(() => of({})), // make sure that the pipeline does not break in case of failed request.
            tap(() => ssoIdBatch.forEach((id) => loadingIds.delete(id))) // remove the ssoids from the loading ones.
          );
        }),
        tap((x) => storeDispatchUsers(x)) // dispatch users.
      )
      .subscribe({
        next: () => {},
        error: (err) => {
          console.error(err);
          register();
        },
      });
  }

  return {
    register: () => {
      if (!isRegistered) {
        register();
        isRegistered = true;
      }
    },
    loadUser: (ssoId) => ssoId && fetchIds$.next(ssoId),
  };
})();

export default Object.freeze(userService);
